import { useRef, useCallback } from 'react'
import { ColumnManager } from '../../../../../utils/ColumnManager'
import { eventBus, EVENTS } from '../../../../../utils/EventBus'
import {
  validateCustomerCodes,
  validateBranchCodes
} from '../../../../../services/Draft'
import {
  validateCustomerCodeLocal,
  normalizeCustomerCode
} from '../../../../../utils/customerValidation'

export function useCustomerCodeRename(
  columnManager,
  distributionManager,
  setNewColumnId,
  sellerCode
) {
  const newColumnInputRefs = useRef({})
  const customerCodeChangeInProgress = useRef(new Set())
  const hasAutoFocused = useRef(new Set())

  const handleCustomerCodeEmptyLabel = useCallback(
    (oldCode) => {
      // User cleared the label to empty string. Instead of only blanking the
      // visual label while leaving the column key intact (which would cause
      // the column to silently retain its claim on the old customer code and
      // trigger a false-positive duplicate error later), we rename the column
      // key so that the customerCode part is removed. The branch (edaban) and
      // index are preserved, allowing later branch-only edits to work cleanly.
      if (oldCode.startsWith('NEW_')) return

      const col = columnManager.getColumnByCustomerCode(oldCode)
      if (!col) return

      const oldLabel = col.label
      if (oldLabel === '') return // already empty, no-op

      const { branchCode, index } = ColumnManager.parseColumnKey(oldCode)
      // New key has an empty customerCode component.
      const newCode = ColumnManager.buildColumnKey('', branchCode, index)

      // If the new key already exists (highly unlikely) fall back to label-only.
      if (newCode !== oldCode && columnManager.hasCustomerCode(newCode)) {
        col.label = ''
        return
      }

      // Perform a real rename: column key + distribution data.
      columnManager.updateCustomerCode(oldCode, newCode)
      col.label = ''
      distributionManager.updateCustomerCode(oldCode, newCode)

      // Keep the DOM input ref in sync so the undo path can update the value.
      const oldInputEl = newColumnInputRefs.current[oldCode]
      if (oldInputEl) {
        newColumnInputRefs.current[newCode] = oldInputEl
        delete newColumnInputRefs.current[oldCode]
      }

      distributionManager.addLogEntry({
        action: `[${distributionManager.username}] 顧客コードを「${oldLabel}」から「」に変更しました`,
        up: () => {
          columnManager.updateCustomerCode(oldCode, newCode)
          const c = columnManager.getColumnByCustomerCode(newCode)
          if (c) c.label = ''
          distributionManager.updateCustomerCode(oldCode, newCode)
          const el =
            newColumnInputRefs.current[oldCode] ??
            newColumnInputRefs.current[newCode]
          if (el) {
            el.value = ''
            newColumnInputRefs.current[newCode] = el
            delete newColumnInputRefs.current[oldCode]
          }
          eventBus.emit(EVENTS.DISTRIBUTION_RESET, distributionManager.data)
        },
        down: () => {
          columnManager.updateCustomerCode(newCode, oldCode)
          const c = columnManager.getColumnByCustomerCode(oldCode)
          if (c) c.label = oldLabel
          distributionManager.updateCustomerCode(newCode, oldCode)
          const el =
            newColumnInputRefs.current[newCode] ??
            newColumnInputRefs.current[oldCode]
          if (el) {
            el.value = oldLabel
            newColumnInputRefs.current[oldCode] = el
            delete newColumnInputRefs.current[newCode]
          }
          eventBus.emit(EVENTS.DISTRIBUTION_RESET, distributionManager.data)
        }
      })
    },
    [columnManager, distributionManager]
  )

  const handleCustomerCodeChange = useCallback(
    async (oldCode, newCode) => {
      const trimmedCode = newCode.trim()
      if (!trimmedCode) return

      const normalizedCode = normalizeCustomerCode(trimmedCode)

      const revertInputValue = () => {
        const input = newColumnInputRefs.current[oldCode]
        if (input) {
          const col = columnManager.getColumnByCustomerCode(oldCode)
          input.value =
            col?.label || (oldCode.startsWith('NEW_') ? '' : oldCode)
        }
      }

      const localError = validateCustomerCodeLocal(normalizedCode)
      if (localError) {
        revertInputValue()
        alert(localError)
        setTimeout(() => {
          const input = newColumnInputRefs.current[oldCode]
          if (input) {
            input.focus()
            input.select()
          }
        }, 0)
        return false
      }

      const isNewColumn = oldCode.startsWith('NEW_')

      if (!isNewColumn) {
        const col = columnManager.getColumnByCustomerCode(oldCode)
        if (!col) return

        const oldLabel = col.label
        if (trimmedCode === oldLabel) return
      }

      // Keep column keys distinct while editing. Save/confirm validates the
      // final reserve, location, branch, and buyer combination.
      const getAvailableIndex = (customerCode, branchCode, excludeKey) => {
        const usedIndexes = new Set(
          columnManager
            .getCustomerColumns()
            .filter((col) => col.customerCode !== excludeKey)
            .map((col) => ColumnManager.parseColumnKey(col.customerCode))
            .filter(
              ({ customerCode: code, branchCode: branch }) =>
                String(code) === String(customerCode) &&
                String(branch) === String(branchCode)
            )
            .map(({ index }) => index)
        )

        let index = 0
        while (usedIndexes.has(index)) index += 1
        return index
      }

      const changeKey = `${oldCode}-${normalizedCode}`
      if (customerCodeChangeInProgress.current.has(changeKey)) return
      customerCodeChangeInProgress.current.add(changeKey)

      try {
        const existingEdaban =
          columnManager.getCustomerHeaderField(oldCode, 'edaban') || ''

        const finalColumnKey = ColumnManager.buildColumnKey(
          normalizedCode,
          existingEdaban,
          getAvailableIndex(normalizedCode, existingEdaban, oldCode)
        )

        if (finalColumnKey === oldCode) {
          setNewColumnId(null)
          hasAutoFocused.current.delete(oldCode)
          return true
        }

        const oldLabel = columnManager.getColumnByCustomerCode(oldCode)?.label
        const orderSnapshot = [...columnManager.order]
        const colId = `customer_${finalColumnKey}`

        const applyUpdate = () => {
          columnManager.updateCustomerCode(oldCode, finalColumnKey)
          const col = columnManager.getColumnByCustomerCode(finalColumnKey)
          if (col) col.label = normalizedCode
          distributionManager.updateCustomerCode(oldCode, finalColumnKey)
          setNewColumnId(null)
          hasAutoFocused.current.delete(oldCode)
        }

        const revertUpdate = () => {
          if (!columnManager.hasCustomerCode(finalColumnKey)) return
          columnManager.updateCustomerCode(finalColumnKey, oldCode)
          const col = columnManager.getColumnByCustomerCode(oldCode)
          if (col) col.label = oldLabel || col.label
          distributionManager.updateCustomerCode(finalColumnKey, oldCode)
          hasAutoFocused.current.delete(oldCode)
        }

        applyUpdate()

        try {
          const validation = await validateCustomerCodes(
            [normalizedCode],
            sellerCode
          )
          if (!validation?.valid) {
            const firstError =
              validation?.errors?.[normalizedCode] ||
              Object.values(validation?.errors || {})[0] ||
              '顧客コードが無効です。'
            revertUpdate()
            revertInputValue()
            alert(firstError)
            setTimeout(() => {
              const input = newColumnInputRefs.current[oldCode]
              if (input) {
                input.focus()
                input.select()
              }
            }, 0)
            return false
          }
        } catch (error) {
          revertUpdate()
          revertInputValue()
          alert('確認に失敗しました。')
          setTimeout(() => {
            const input = newColumnInputRefs.current[oldCode]
            if (input) {
              input.focus()
              input.select()
            }
          }, 0)
          return false
        }

        distributionManager.addLogEntry({
          action: isNewColumn
            ? `[${distributionManager.username}] 顧客列を追加しました：${normalizedCode}`
            : `[${distributionManager.username}] 顧客コードを「${oldLabel}」から「${normalizedCode}」に変更しました`,
          up: () => {
            columnManager.updateCustomerCode(oldCode, finalColumnKey)
            const col = columnManager.getColumnByCustomerCode(finalColumnKey)
            if (col) col.label = normalizedCode
            distributionManager.updateCustomerCode(oldCode, finalColumnKey)
            eventBus.emit(EVENTS.DISTRIBUTION_RESET, distributionManager.data)
          },
          down: () => {
            columnManager.updateCustomerCode(finalColumnKey, oldCode)
            const col = columnManager.getColumnByCustomerCode(oldCode)
            if (col) col.label = oldLabel || (isNewColumn ? '' : col.label)
            distributionManager.updateCustomerCode(finalColumnKey, oldCode)
            eventBus.emit(EVENTS.DISTRIBUTION_RESET, distributionManager.data)
          }
        })

        return true
      } finally {
        setTimeout(
          () => customerCodeChangeInProgress.current.delete(changeKey),
          100
        )
      }
    },
    [columnManager, distributionManager, setNewColumnId, sellerCode]
  )

  return {
    newColumnInputRefs,
    hasAutoFocused,
    handleCustomerCodeChange,
    handleCustomerCodeEmptyLabel
  }
}
